import { debounce } from 'lodash'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type FC,
} from 'react'
import { Tagged } from 'type-fest'
import {
  type CollaborationTokenCarrier,
  type CollaborationToken,
  type DocumentId,
} from './CollaborationTextEditor/types'
import {
  getCollaborationToken,
  isCollaborationTokenExpiring,
} from './CollaborationTextEditor/utils'

/**
 * Signals that a collaboration token has been requested, but the server has not
 * yet responded.
 */
export type PendingCollaborationToken = Tagged<CollaborationToken, 'Pending'>

export const PENDING_COLLABORATION_TOKEN =
  'pending-collaboration-token' as PendingCollaborationToken

const CollaborationContext = createContext<{
  documentIds: string[]
  getToken: () => Promise<CollaborationToken | PendingCollaborationToken>
}>({ documentIds: [], getToken: async () => PENDING_COLLABORATION_TOKEN })

type ProviderProps = Readonly<{
  documentIds?: DocumentId | DocumentId[]
}>
/**
 * `<TextEditorTokenProvider>` sets up the `useCollaborationToken` hook, which
 * provides a single `token` that can be used for all the given `documentIds`.
 *
 * Because the token is valid for a specific set of `documentIds` the
 * `documentIds` prop needs to be updated when the set of text sections changes.
 */
export const TextEditorTokenProvider: FC<
  React.PropsWithChildren<ProviderProps>
> = ({ children, documentIds = [] }) => {
  const tokenData = useRef<
    CollaborationTokenCarrier & { documentIds: DocumentId[] }
  >({
    token: PENDING_COLLABORATION_TOKEN,
    expiresAt: null,
    receivedAt: null,
    documentIds: [],
  })

  const wrappedDocumentIds = useMemo(() => {
    return Array.isArray(documentIds) ? documentIds : [documentIds]
  }, [documentIds])

  // This function is debounced so if multiple text editors reauth at once,
  // they'll all get the same token
  const getTokenDebounced = useMemo(
    () =>
      debounce(
        async (wrappedDocumentIds: DocumentId[]) => {
          if (wrappedDocumentIds.length === 0) {
            return PENDING_COLLABORATION_TOKEN
          }

          const { token, documentIds: tokenDocumentIds } = tokenData.current

          if (
            token !== PENDING_COLLABORATION_TOKEN &&
            !isCollaborationTokenExpiring(tokenData.current) &&
            wrappedDocumentIds.every((id) => tokenDocumentIds.includes(id))
          ) {
            return token
          }

          const newTokenData = await getCollaborationToken(wrappedDocumentIds)
          tokenData.current = {
            ...newTokenData,
            documentIds: wrappedDocumentIds,
          }
          return newTokenData.token
        },
        1000 * 5, // 5 seconds, expected timeframe for token call to complete
        { leading: true }
      ),
    []
  )

  const getToken = useCallback(
    async () => getTokenDebounced(wrappedDocumentIds),
    [getTokenDebounced, wrappedDocumentIds]
  )

  // Fetch token on mount so it's ready when the editors are initialized
  useEffect(() => {
    getTokenDebounced(wrappedDocumentIds)
    return () => getTokenDebounced.cancel()
  }, [getTokenDebounced, wrappedDocumentIds])

  return (
    <CollaborationContext.Provider
      value={{
        documentIds: wrappedDocumentIds,
        getToken,
      }}
    >
      {children}
    </CollaborationContext.Provider>
  )
}
TextEditorTokenProvider.displayName = 'Boards: TextEditorTokenProvider ← Board'

/**
 * Access the `<TextEditorTokenProvider>` token for the given `documentId`
 * within `<TextEditorTokenProvider>`’s component tree. If the tree’s token
 * isn’t valid for `documentId` this returns `undefined` and the token must
 * be retrieved for the individual `documentId`.
 */
export const useCollaborationTokenProvider = (documentId: DocumentId) => {
  const { documentIds, getToken } = useContext(CollaborationContext)
  if (!documentId || !documentIds.includes(documentId)) return
  return getToken
}
